import {DateTime} from 'luxon';
import {
  AssetFolderEntityType,
  CreativeAssetPurposeOld,
  AssetTypeOld,
  AssetType,
  DerivedFilePurpose,
} from './Assets.enums';

export interface AssetGeneralMetadataOld {
  title: string;
  description: string;
  purpose?: CreativeAssetPurposeOld;
  usageGuides: string;
}

export interface CreativeAssetSemanticMetadataOld {
  objects: string[];
  sceneAndMood: string;
  colorPalette: string[];
  style: string;
}

export interface AssetTechnicalMetadataOld {
  fileSize?: number; // File size in bytes
  // Add technical metadata properties as needed
  // This interface is intentionally minimal for now
  [key: string]: unknown;
}

export interface AssetMetadataOld {
  schemaVersion: number;
  general: AssetGeneralMetadataOld;
  semantic: CreativeAssetSemanticMetadataOld;
  technical: AssetTechnicalMetadataOld;
}

export interface AssetOld {
  id: string;
  name: string;
  url: string;
  assetType: AssetTypeOld;
  folderPath: string;
  createTime: DateTime;
  updateTime: DateTime;
  createdBy: number;
  updatedBy: number;
  metadata?: AssetMetadataOld;
}

// Type alias for backward compatibility
export type CreativeAssetOld = AssetOld;

// Request types for new Assets API
export interface GenerateZipRequest {
  assetIds?: number[] | null;
  folderIds?: number[] | null;
}

export interface NewFolderRequest {
  name?: string | null;
}

export interface NewAssetCreationgRequestForFolder {
  fileUrls?: string[] | null;
}

export interface FolderAndAssetRequestItem {
  assetUrl: string;
  assetPath: string;
}
export interface MoveAssetRequest {
  folderId: number;
}

export interface LinkAssetRequest {
  entityType: AssetFolderEntityType;
  folderId: number;
}

// Remote types for new Assets API - matching AssetFolderTree_UI structure
export interface RemoteImageMetadata {
  width: number;
  height: number;
}

export interface RemoteVideoMetadata {
  width: number;
  height: number;
  lengthSec: number; // duration in seconds (float)
  bitrate: number;
  fps: number;
}

export interface RemoteAudioMetadata {
  bitrate: number;
  channels: number;
  sampleRate: number;
  lengthSec: number; // duration in seconds (float)
}

export interface RemoteTechnicalMetadata {
  fileSizeInBytes: number;
  fileUrl?: string | null;
  fileHash: number;
  imageMetadata?: RemoteImageMetadata | null;
  videoMetadata?: RemoteVideoMetadata | null;
  audioMetadata?: RemoteAudioMetadata | null;
}

export interface RemoteCustomMetadata {
  tags?: Record<string, string | null> | null;
}

export interface RemoteAssetFile {
  derivedFilePurpose: DerivedFilePurpose;
  technicalMetadata: RemoteTechnicalMetadata | null;
}

export interface RemoteAiAssetMetadataV1 {
  title?: string | null;
  description?: string | null;
  objects?: string[] | null;
  sceneAndMood?: string | null;
  colorPalette?: string[] | null;
  style?: string | null;
}

export interface RemoteAiContentMetadata {
  state: number; // AiAssetContentState enum value
  stateUpdateTime: string; // date-time
  errorMessage?: string | null;
  metadataV1?: RemoteAiAssetMetadataV1 | null;
}

export interface RemoteManualContentMetadata {
  purpose?: number | null; // AssetPurpose enum value
  usageGuides?: string | null;
}

export interface RemoteDetectedObjectContentMetadata {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface RemoteContentMetadata {
  manualContentMetadata: RemoteManualContentMetadata;
  aiContentMetadata: RemoteAiContentMetadata;
  detectedObjectsContentMetadata?: Record<
    string,
    RemoteDetectedObjectContentMetadata[] | null
  > | null;
}

export enum PosterFilePurpose {
  StaticImage = 0,
  AnimatedImage = 1,
}

export interface PosterFile {
  posterFilePurpose: PosterFilePurpose;
  fileUrl: string;
}
export interface RemoteAsset {
  id: number;
  clientId: number;
  originalfile: RemoteTechnicalMetadata;
  customMetadata: Record<string, string | null>;
  name?: string | null;
  extension?: string | null;
  assetType: string; // AssetType enum value
  derivedFiles: RemoteAssetFile[];
  posterFiles: PosterFile[];
  contentMetadata: RemoteContentMetadata;
  createdTime: string; // date-time
  updatedTime?: string | null; // date-time
  createdBy: number;
  updatedBy?: number | null;
}

export interface RemoteFolder {
  folderId: number;
  folderName?: string | null;
  folderPath?: string | null;
  createdTime: string; // date-time
  createdBy: number;
  folders?: RemoteFolder[] | null;
  assets?: RemoteAsset[] | null;
  isChildrenLoaded?: boolean; // Flag indicating if folder contents have been attempted to load (from backend)
  isMatchSearchTerm?: boolean | null; // Flag indicating if folder name matches the search term
}

// Type alias for backward compatibility
export type RemoteAssetFolderTree = RemoteFolder;

// Client types for new Assets API
export interface ImageMetadata {
  width: number;
  height: number;
}

export interface VideoMetadata {
  width: number;
  height: number;
  lengthSec: number; // duration in seconds
  bitrate: number;
  fps: number;
}

export interface AudioMetadata {
  bitrate: number;
  channels: number;
  sampleRate: number;
  lengthSec: number; // duration in seconds
}

export interface TechnicalMetadata {
  fileSizeInBytes: number;
  fileUrl?: string | null;
  fileHash: number;
  imageMetadata?: ImageMetadata | null;
  videoMetadata?: VideoMetadata | null;
  audioMetadata?: AudioMetadata | null;
}

export interface CustomMetadata {
  tags?: Record<string, string | null> | null;
}

export interface AssetFile {
  derivedFilePurpose: DerivedFilePurpose;
  technicalMetadata: TechnicalMetadata | null;
}

export interface AiAssetMetadataV1 {
  title?: string | null;
  description?: string | null;
  objects?: string[] | null;
  sceneAndMood?: string | null;
  colorPalette?: string[] | null;
  style?: string | null;
}

export interface AiContentMetadata {
  state: number; // AiAssetContentState enum value
  stateUpdateTime?: DateTime | null;
  errorMessage?: string | null;
  metadataV1?: AiAssetMetadataV1 | null;
}

export interface ManualContentMetadata {
  purpose?: number | null; // AssetPurpose enum value
  usageGuides?: string | null;
}

export interface DetectedObjectContentMetadata {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ContentMetadata {
  manualContentMetadata: ManualContentMetadata;
  aiContentMetadata: AiContentMetadata;
  detectedObjectsContentMetadata?: Record<string, DetectedObjectContentMetadata[] | null> | null;
}

export interface Asset {
  id: number;
  clientId: number;
  originalfile: TechnicalMetadata;
  customMetadata: Record<string, string | null>;
  name?: string | null;
  extension?: string | null;
  assetType: AssetType;
  derivedFiles: AssetFile[];
  posterFiles: PosterFile[];
  contentMetadata: ContentMetadata | null;
  createdTime: DateTime;
  updatedTime?: DateTime | null;
  createdBy: number;
  updatedBy?: number | null;
}

export interface Folder {
  folderId: number;
  folderName?: string | null;
  folderPath?: string | null;
  createdTime: DateTime;
  createdBy: number;
  folders?: Folder[];
  assets?: Asset[];
  isChildrenLoaded?: boolean; // Flag indicating if folder contents have been attempted to load (from backend)
  isMatchSearchTerm?: boolean | null; // Flag indicating if folder name matches the search term (for highlighting in search results)
}

export interface ZipUrlResponse {
  url: string;
}

/**
 * Shared state interface for asset display components across all contexts
 * Uses normalized state structure - only stores IDs, derive folder objects from assetTree
 */
export interface AssetDisplayState {
  // Tree structure (single source of truth - lazy-loaded: folders always present, assets loaded on-demand)
  assetTree: Folder | null;

  // Navigation state (normalized - only ID, derive folder path using buildFolderPathIds utility)
  currentFolderId: number | null;

  // Selection state (normalized - only IDs)
  selectedAssetIds: number[];
  selectedFolderIds: number[];

  // Search state
  searchTerm: string;
  searchType: 'name' | 'content';
  searchResults: Folder | null; // Filtered folder tree from search (parallel to assetTree)

  // Loading states
  isLoadingTree: boolean; // Loading initial tree structure
  isLoadingFolderAssets: boolean; // Loading assets for a specific folder
  isSearching: boolean;
}

/**
 * Initial state for AssetDisplayState - shared across all contexts
 */
export const initialAssetDisplayState: AssetDisplayState = {
  assetTree: null,
  currentFolderId: null,
  selectedAssetIds: [],
  selectedFolderIds: [],
  searchTerm: '',
  searchType: 'name',
  searchResults: null,
  isLoadingTree: false,
  isLoadingFolderAssets: false,
  isSearching: false,
};

export {AssetFolderEntityType};
